import {
  ModDivider,
  ModMenuItem,
  ModSkeleton
} from '@moderneinc/design-system-components';
import {
  iconSize,
  semanticColors,
  typography
} from '@moderneinc/design-system-tokens';
import { Box, MenuList, Typography } from '@mui/material';
import NextLink from 'next/link';
import React, { type FunctionComponent, useContext } from 'react';
import { ConfigurationContext } from '../../../contexts/config.context';
import { useHelpMenuCustomizations } from '../../../hooks/use-help-menu-customizations.hooks';
import { PlayIcon } from '../../../icons/icons';
import { useTourEngineStore } from '../../../stores/tour-engine.store';
import { ModerneCli } from '../../account-menu/moderne-cli/moderne-cli.component';
import { SectionHeading } from '../../global-search/global-search.styled';
import { DAY_ZERO_TOUR_ID } from '../../guided-tour/day-zero-tour.config';
import { FlexBox } from '../../styled-components/layouts/layouts.styled';
import {
  FALLBACK_HELP_LINKS,
  type HelpMenuLink,
  RESOURCE_LINKS,
  TOOL_LINKS
} from './help-menu.data';

type LinkMenuItemProps = {
  link: HelpMenuLink;
  icon?: React.ReactNode;
  onSelect?: () => void;
};

const LinkMenuItem: FunctionComponent<LinkMenuItemProps> = ({
  link,
  icon,
  onSelect
}) => {
  if (link.external) {
    return (
      <ModMenuItem
        component="a"
        href={link.getHref()}
        target="_blank"
        rel="noopener noreferrer"
        icon={icon}
        onClick={onSelect}
      >
        {link.label}
      </ModMenuItem>
    );
  }
  return (
    <ModMenuItem
      component={NextLink}
      href={link.getHref()}
      icon={icon}
      onClick={onSelect}
    >
      {link.label}
    </ModMenuItem>
  );
};

type HelpMenuContentProps = {
  showVersion?: boolean;
  /** Called when a non-navigating item (e.g. Getting started tour) is selected,
      so the containing menu/popover can close itself. */
  onItemSelect?: () => void;
};

export const HelpMenuContent: FunctionComponent<HelpMenuContentProps> = ({
  showVersion = true,
  onItemSelect
}) => {
  const { moreHelp, cliDownload, loading } = useHelpMenuCustomizations();
  const resume = useTourEngineStore((state) => state.resume);

  return (
    <React.Fragment>
      <MenuList>
        <ModMenuItem
          icon={
            <Box
              component="span"
              sx={{ display: 'inline-flex', color: 'primary.main' }}
            >
              <PlayIcon size={iconSize.md} />
            </Box>
          }
          sx={{ color: 'primary.main' }}
          onClick={() => {
            resume(DAY_ZERO_TOUR_ID);
            onItemSelect?.();
          }}
        >
          Getting started tour
        </ModMenuItem>
      </MenuList>
      <ModDivider
        flexItem
        variant="fullWidth"
        sx={{ borderColor: semanticColors.color.border.primary }}
      />
      <SectionHeading
        sx={{
          minWidth: '15rem',
          fontSize: typography.fontSize.default,
          color: semanticColors.color.status.neutral.fg,
          padding: '10px 16px 0'
        }}
      >
        Resources
      </SectionHeading>
      <MenuList>
        {RESOURCE_LINKS.map((link) => (
          <LinkMenuItem key={link.label} link={link} onSelect={onItemSelect} />
        ))}
      </MenuList>
      <ModDivider
        flexItem
        variant="fullWidth"
        sx={{ borderColor: semanticColors.color.border.primary }}
      />
      <SectionHeading
        sx={{
          minWidth: '15rem',
          fontSize: typography.fontSize.default,
          color: semanticColors.color.status.neutral.fg,
          padding: '10px 16px 0'
        }}
      >
        Tools
      </SectionHeading>
      <MenuList>
        {TOOL_LINKS.map((link) => (
          <LinkMenuItem key={link.label} link={link} onSelect={onItemSelect} />
        ))}
        <ModerneCli cliLinkOverride={cliDownload} />
      </MenuList>
      <ModDivider
        flexItem
        variant="fullWidth"
        sx={{ borderColor: semanticColors.color.border.primary }}
      />
      <SectionHeading
        sx={{
          minWidth: '15rem',
          fontSize: typography.fontSize.default,
          color: semanticColors.color.status.neutral.fg,
          padding: '10px 16px 0'
        }}
      >
        Need more help?
      </SectionHeading>
      {loading && <ModSkeleton width="100%" height="3rem" />}
      {!loading && !moreHelp && (
        <MenuList>
          {FALLBACK_HELP_LINKS.map((link) => {
            const Icon = link.icon;
            return (
              <LinkMenuItem
                key={link.label}
                link={link}
                icon={Icon ? <Icon /> : undefined}
                onSelect={onItemSelect}
              />
            );
          })}
        </MenuList>
      )}

      {!loading && moreHelp && (
        <MenuList>
          {moreHelp.map((helpItem, index) => (
            <ModMenuItem
              key={index}
              component="a"
              href={helpItem.uri}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onItemSelect}
            >
              {helpItem.label}
            </ModMenuItem>
          ))}
        </MenuList>
      )}

      {showVersion && (
        <React.Fragment>
          <ModDivider />
          <ModerneVersion />
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

const ModerneVersion: FunctionComponent = () => {
  const {
    config: { version }
  } = useContext(ConfigurationContext);

  return (
    <FlexBox
      sx={{
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 1,
        py: 2,
        px: 2
      }}
    >
      <Typography
        sx={{
          fontSize: typography.fontSize.xs,
          lineHeight: 1.5,
          color: semanticColors.color.text.primary
        }}
      >
        &copy; {new Date().getFullYear()} Moderne, Inc.
      </Typography>
      {version && (
        <Typography
          sx={{
            fontSize: typography.fontSize.xs,
            lineHeight: 1.5,
            color: semanticColors.color.text.primary
          }}
        >
          Version: {version}
        </Typography>
      )}
    </FlexBox>
  );
};
